/**
 * Sentry bootstrap for the admin panel.
 *
 * Pulls @sentry/react via a dynamic import inside initSentry() so the
 * SDK only loads when a DSN is configured. Browsers tree-shake the
 * static reference but still bundle it; the dynamic import keeps the
 * SDK out of the main chunk when telemetry isn't set up locally.
 *
 * To enable on an environment:
 *   1. set `VITE_SENTRY_DSN=https://...@sentry.io/<project>` in the
 *      build's env file (e.g. `.env.production`)
 *   2. rebuild the admin bundle
 *
 * Build-time env vars:
 *   VITE_SENTRY_DSN                    — project DSN (no DSN => silent no-op)
 *   VITE_SENTRY_TRACES_SAMPLE_RATE     — 0..1, defaults to 0.1
 *   VITE_SENTRY_REPLAY_SAMPLE_RATE     — session replay rate, defaults to 0
 *   VITE_SENTRY_ENVIRONMENT            — tag, falls back to import.meta.env.MODE
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
let sentry: any = null;

export async function initSentry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  try {
    const Sentry = await import("@sentry/react");
    Sentry.init({
      dsn,
      environment:
        (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
        (import.meta.env.MODE as string | undefined) ||
        "development",
      tracesSampleRate: Number(
        import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0.1",
      ),
      replaysSessionSampleRate: Number(
        import.meta.env.VITE_SENTRY_REPLAY_SAMPLE_RATE ?? "0",
      ),
      // Replays on errors are cheap and high-signal even when normal
      // replays are off — captures the last 60s before a crash.
      replaysOnErrorSampleRate: 1.0,
      // Skip user-input validation errors; we only want to know when
      // the admin panel itself misbehaves.
      beforeSend(event) {
        const msg = event?.exception?.values?.[0]?.value || "";
        if (/ValidationError|Network request failed|AbortError/i.test(msg)) {
          return null;
        }
        return event;
      },
    });
    sentry = Sentry;
    // eslint-disable-next-line no-console
    console.info("[sentry] initialized");
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[sentry] init skipped:", (err as Error).message);
    sentry = null;
  }
}

export function captureException(
  err: unknown,
  context?: Record<string, unknown>,
): void {
  if (!sentry) return;
  try {
    sentry.captureException(err, context ? { extra: context } : undefined);
  } catch {
    /* never let telemetry break the UI */
  }
}

export function captureMessage(
  message: string,
  level: "info" | "warning" | "error" = "info",
): void {
  if (!sentry) return;
  try {
    sentry.captureMessage(message, level);
  } catch {
    /* swallow */
  }
}
